import React, { useEffect, useMemo, useState } from 'react';
import {
  Box,
  Button,
  Drawer,
  FormControl,
  IconButton,
  MenuItem,
  Select,
  TextField,
  Typography,
} from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';
import useApiControl from '../../../common/useApiControl';

function TagSearchSelect() {
  const [tags, setTags] = useState([]);
  const [selectedTag, setSelectedTag] = useState('');
  const [keyword, setKeyword] = useState('');
  const [isMobile, setIsMobile] = useState(window.innerWidth <= 1100);
  const [drawerOpen, setDrawerOpen] = useState(false);

  const { data, executeRequest } = useApiControl({
    action: '/get_master_tags',
    method: 'POST',
  });

  useEffect(() => {
    const handleResize = () => setIsMobile(window.innerWidth <= 1100);
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  useEffect(() => {
    const localTags = localStorage.getItem('tags');
    if (localTags) {
      try {
        const parsed = JSON.parse(localTags);
        setTags(parsed);
        return;
      } catch (error) {
        console.error('tags parse error', error);
      }
    }

    executeRequest().catch((error) => {
      console.error(error);
    });
  }, []);

  useEffect(() => {
    if (data?.tags) {
      localStorage.setItem('tags', JSON.stringify(data.tags));
      setTags(data.tags);
    }
  }, [data]);

  const searchHref = useMemo(() => {
    const params = new URLSearchParams();
    const normalizedKeyword = keyword.trim();

    if (selectedTag) {
      params.set('tag', selectedTag);
    }

    if (normalizedKeyword) {
      params.set('keyword', normalizedKeyword);
    }

    const query = params.toString();
    return query ? `./search?${query}` : './search';
  }, [selectedTag, keyword]);

  const moveSearchPage = () => {
    window.location.href = searchHref;
  };

  const selectMenuProps = {
    sx: {
      zIndex: 1802,
    },
    PaperProps: {
      sx: {
        zIndex: 1802,
      },
    },
  };

  const desktopSearchBox = (
    <Box sx={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
      <SearchIcon fontSize="small" />
      <FormControl size="small" sx={{ minWidth: { xs: '90px', sm: '130px' } }}>
        <Select
          displayEmpty
          value={selectedTag}
          onChange={(event) => setSelectedTag(String(event.target.value))}
          MenuProps={selectMenuProps}
          sx={{
            color: '#fff',
            height: '32px',
            '.MuiSelect-icon': { color: '#fff' },
            '.MuiOutlinedInput-notchedOutline': { borderColor: 'rgba(255,255,255,0.6)' },
            '&:hover .MuiOutlinedInput-notchedOutline': { borderColor: '#fff' },
            '&.Mui-focused .MuiOutlinedInput-notchedOutline': { borderColor: '#fff' },
            backgroundColor: 'rgba(255, 255, 255, 0.08)',
          }}
        >
          <MenuItem value="">タグ指定なし</MenuItem>
          {tags.map((tag) => (
            <MenuItem key={tag.id} value={String(tag.id)}>
              {tag.name}
            </MenuItem>
          ))}
        </Select>
      </FormControl>
      <TextField
        value={keyword}
        onChange={(event) => setKeyword(event.target.value)}
        placeholder="ルーム名 / ユーザー名"
        size="small"
        sx={{
          minWidth: { sm: '160px', md: '200px' },
          '& .MuiInputBase-root': {
            color: '#fff',
            height: '32px',
            backgroundColor: 'rgba(255, 255, 255, 0.08)',
          },
          '& .MuiOutlinedInput-notchedOutline': {
            borderColor: 'rgba(255,255,255,0.6)',
          },
          '& .MuiInputBase-root:hover .MuiOutlinedInput-notchedOutline': {
            borderColor: '#fff',
          },
          '& .MuiInputBase-root.Mui-focused .MuiOutlinedInput-notchedOutline': {
            borderColor: '#fff',
          },
          '& .MuiInputBase-input::placeholder': {
            color: 'rgba(255,255,255,0.85)',
            opacity: 1,
          },
        }}
      />
      <Button variant="outlined" color="inherit" size="small" onClick={moveSearchPage}>
        検索
      </Button>
    </Box>
  );

  if (!isMobile) {
    return desktopSearchBox;
  }

  return (
    <>
      <IconButton color="inherit" aria-label="search" onClick={() => setDrawerOpen(true)}>
        <SearchIcon />
      </IconButton>
      <Drawer
        anchor="right"
        open={drawerOpen}
        onClose={() => setDrawerOpen(false)}
        PaperProps={{
          sx: {
            width: 280,
            maxWidth: '100%',
            boxSizing: 'border-box',
            zIndex: 1701,
            padding: '16px 12px',
          },
        }}
        ModalProps={{
          keepMounted: true,
          sx: {
            zIndex: 1700,
          },
        }}
      >
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
          <Typography variant="subtitle1" sx={{ fontWeight: 'bold' }}>
            検索
          </Typography>
          <FormControl size="small" fullWidth>
            <Select
              displayEmpty
              value={selectedTag}
              onChange={(event) => setSelectedTag(String(event.target.value))}
              MenuProps={selectMenuProps}
            >
              <MenuItem value="">タグ指定なし</MenuItem>
              {tags.map((tag) => (
                <MenuItem key={tag.id} value={String(tag.id)}>
                  {tag.name}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
          <TextField
            value={keyword}
            onChange={(event) => setKeyword(event.target.value)}
            placeholder="ルーム名 / ユーザー名"
            size="small"
            fullWidth
          />
          <Button
            variant="contained"
            onClick={() => {
              moveSearchPage();
              setDrawerOpen(false);
            }}
          >
            検索
          </Button>
        </Box>
      </Drawer>
    </>
  );
}

export default TagSearchSelect;
